/**
 * Contexto de Autenticação com Firebase
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { api } from '../services/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAdmin: boolean;
  isApproved: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isApproved, setIsApproved] = useState(false);

  useEffect(() => {
    // Listener para mudanças no estado de autenticação
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        
        // Sincronizar usuário com backend
        try {
          const response = await api.auth.syncUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email || '',
            displayName: firebaseUser.displayName || '',
            photoURL: firebaseUser.photoURL || '',
          });
          
          // Verificar se é admin e se está aprovado
          setIsAdmin(response.user?.isAdmin === 1 || response.user?.isAdmin === true);
          setIsApproved(response.user?.isApproved === 1 || response.user?.isApproved === true);
          
          console.log('👤 Usuário sincronizado:', {
            email: firebaseUser.email,
            isAdmin: response.user?.isAdmin,
            isApproved: response.user?.isApproved
          });
        } catch (error) {
          console.error('Erro ao sincronizar usuário:', error);
          setIsAdmin(false);
          setIsApproved(false);
        }
      } else {
        setUser(null);
        setIsAdmin(false);
        setIsApproved(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signInWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Login com Google bem-sucedido:', result.user.email);
    } catch (error: any) {
      console.error('Erro no login com Google:', error);
      throw new Error(error.message || 'Erro ao fazer login com Google');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      console.log('Logout realizado com sucesso');
    } catch (error: any) {
      console.error('Erro no logout:', error);
      throw new Error(error.message || 'Erro ao fazer logout');
    }
  };

  const value = {
    user,
    loading,
    isAdmin,
    isApproved,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
